import { Card } from 'components';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { BuyPlanCompany } from '../types/Add-subscription.types';

interface CompanySelectorProps {
  companies: BuyPlanCompany[];
  /** null = sin elección (obligatoria, sin valor por defecto). */
  selectedCompanyId: number | null;
  userCompanyId: number;
  disabled?: boolean;
  onChangeCompany: (companyId: number) => void;
}

/** Se muestra el alias de la compañía si existe; si no, el name. */
const getCompanyLabel = (company: BuyPlanCompany) =>
  company.alias_name?.trim() || company.name;

const USER_COMPANY_SUFFIX = ' — compañía del usuario';

/** Minúsculas y sin acentos, para buscar "compania" y encontrar "Compañía". */
const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/** Ids para vincular el campo de búsqueda (combobox) con la lista y su opción activa. */
const LISTBOX_ID = 'buy-plan-company-listbox';
const getOptionId = (companyId: number) =>
  `buy-plan-company-option-${companyId}`;

/** Ordena alfabéticamente por la etiqueta que se muestra. */
const sortCompanies = (companies: BuyPlanCompany[]) =>
  [...companies].sort((a, b) =>
    getCompanyLabel(a).localeCompare(getCompanyLabel(b), 'es', {
      sensitivity: 'base',
    })
  );

/** Filtra por nombre, alias o id, sin distinguir mayúsculas ni acentos. */
const filterCompanies = (companies: BuyPlanCompany[], search: string) => {
  const term = normalize(search);
  if (!term) return companies;
  return companies.filter((company) =>
    normalize(
      `${company.name} ${company.alias_name ?? ''} ${company.id}`
    ).includes(term)
  );
};

/**
 * Selector de compañía de buy plan (solo agentes): define con qué prices se lista y con qué
 * company se crea la sub. Lista en orden alfabético con buscador (nombre, alias o id).
 */
export const CompanySelector = ({
  companies,
  selectedCompanyId,
  userCompanyId,
  disabled = false,
  onChangeCompany,
}: CompanySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const sortedCompanies = useMemo(() => sortCompanies(companies), [companies]);
  const filteredCompanies = useMemo(
    () => filterCompanies(sortedCompanies, search),
    [sortedCompanies, search]
  );
  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  /** Cierra la lista y limpia la búsqueda. */
  const close = () => {
    setIsOpen(false);
    setSearch('');
  };

  /** Abre la lista con la compañía seleccionada como opción activa. */
  const open = () => {
    if (disabled) return;
    const selectedIndex = sortedCompanies.findIndex(
      (c) => c.id === selectedCompanyId
    );
    setActiveIndex(Math.max(selectedIndex, 0));
    setIsOpen(true);
  };

  /** Elige una compañía; solo avisa si cambió. */
  const select = (company: BuyPlanCompany) => {
    close();
    if (company.id !== selectedCompanyId) onChangeCompany(company.id);
  };

  // Cierra al hacer click fuera del selector.
  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  // Mantiene visible la opción activa al navegar con el teclado.
  useEffect(() => {
    if (!isOpen) return;
    const option = listRef.current?.children[activeIndex] as
      | HTMLElement
      | undefined;
    option?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  /** Filtra la lista y vuelve a la primera opción. */
  const onSearchChange = (value: string) => {
    setSearch(value);
    setActiveIndex(0);
  };

  /** Flechas mueven la opción activa, Enter elige y Escape cierra; el foco queda en el campo. */
  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filteredCompanies.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const company = filteredCompanies[activeIndex];
      if (company) select(company);
    } else if (event.key === 'Escape') {
      close();
    }
  };

  if (companies.length === 0) return null;

  return (
    <Card WrapperClassName="add-subscription__company">
      <span className="add-subscription__company-label" id="buy-plan-company">
        Compañía
      </span>
      <div className="company-selector" ref={containerRef}>
        <button
          type="button"
          className="company-selector__trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-labelledby="buy-plan-company"
          disabled={disabled}
          onClick={() => (isOpen ? close() : open())}
        >
          {selectedCompany
            ? getCompanyLabel(selectedCompany)
            : 'Elige una compañía'}
          {selectedCompanyId === userCompanyId && USER_COMPANY_SUFFIX}
        </button>

        {isOpen && (
          <div className="company-selector__dropdown">
            <input
              ref={searchRef}
              type="text"
              className="company-selector__search"
              placeholder="Buscar compañía..."
              aria-label="Buscar compañía"
              role="combobox"
              aria-expanded={filteredCompanies.length > 0}
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={
                filteredCompanies[activeIndex]
                  ? getOptionId(filteredCompanies[activeIndex].id)
                  : undefined
              }
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={onSearchKeyDown}
            />
            {filteredCompanies.length === 0 ? (
              <p className="company-selector__empty">
                No hay compañías que coincidan con &quot;{search}&quot;.
              </p>
            ) : (
              <ul
                ref={listRef}
                id={LISTBOX_ID}
                className="company-selector__list"
                role="listbox"
                aria-labelledby="buy-plan-company"
              >
                {filteredCompanies.map((company, index) => (
                  <li
                    key={company.id}
                    id={getOptionId(company.id)}
                    role="option"
                    aria-selected={company.id === selectedCompanyId}
                    className={[
                      'company-selector__option',
                      index === activeIndex
                        ? 'company-selector__option--active'
                        : '',
                      company.id === selectedCompanyId
                        ? 'company-selector__option--selected'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => select(company)}
                  >
                    {getCompanyLabel(company)}
                    {company.id === userCompanyId && USER_COMPANY_SUFFIX}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {selectedCompanyId !== null && selectedCompanyId !== userCompanyId && (
        <p className="add-subscription__company-note">
          La suscripción se creará con esta compañía; la compañía del usuario no
          cambia.
        </p>
      )}
    </Card>
  );
};
